import React from 'react';
import { FaEnvelope, FaGlobe, FaLinkedin, FaWhatsapp } from 'react-icons/fa';
import { Link } from 'react-router-dom';

export default function Footer({ language = 'en' }) {
  const translations = {
    en: {
      companyName: "Infynix Solution",
      companyDesc:
        "Infynix Solution is a trusted technology partner offering Customized ERP Solutions, VPS Servers, AI Automation, Mobile App Development, and Web Development. We empower startups, SMEs, and enterprises with secure, scalable, and performance-driven digital systems that streamline operations and accelerate growth.",
      contactHeading: "Contact",
      whatsappNumber: "919281407673",
      contactEmail: "info@infynixsolution.com",
      contactWebsite: "www.infynixsolution.com",
      companyHeading: "Company",
      companyLinks: {
        home: "Home",
        services: "Services",
        about: "About",
        contact: "Contact",
      },
      bottomText: `© ${new Date().getFullYear()} Infynix Solution. All rights reserved.`,
      designedBy: "Designed and Developed by Mohammad Sahil Ansari",
      designerEmail: "ansarisahileager@gmail.com",
    },
    ar: {
      companyName: "إنفينكس سوليوشن",
      companyDesc:
        "إنفينكس سوليوشن هي شريك تكنولوجي موثوق يقدم حلول ERP مخصصة، وخوادم VPS، وأتمتة الذكاء الاصطناعي، وتطوير تطبيقات الهواتف المحمولة، وتطوير المواقع الإلكترونية. نحن نمكن الشركات الناشئة والصغيرة والمتوسطة والكبيرة من أنظمة رقمية آمنة وقابلة للتوسع وعالية الأداء، مما يبسط العمليات ويعزز النمو.",
      contactHeading: "الاتصال",
      whatsappNumber: "919281407673",
      contactEmail: "info@infynixsolution.com",
      contactWebsite: "www.infynixsolution.com",
      companyHeading: "الشركة",
      companyLinks: {
        home: "الرئيسية",
        services: "الخدمات",
        about: "من نحن",
        contact: "الاتصال",
      },
      bottomText: `© ${new Date().getFullYear()} إنفينكس سوليوشن. جميع الحقوق محفوظة.`,
      designedBy: "صمم وطور بواسطة Mohammad Sahil Ansari",
      designerEmail: "ansarisahileager@gmail.com",
    },
  };

  const currentLang = translations[language] || translations.en;

  const onlyDigits = (s = '') => (s || '').toString().replace(/\D/g, '');

  const formatWhatsAppDisplay = (num) => {
    const s = onlyDigits(num);
    if (!s) return num;
    if (s.length === 12) {
      const cc = `+${s.slice(0, 2)}`;
      const a = s.slice(2, 6);
      const b = s.slice(6, 9);
      const c = s.slice(9);
      return `${cc} ${a} ${b} ${c}`;
    }
    if (s.length === 11) {
      const cc = `+${s.slice(0, 1)}`;
      return `${cc} ${s.slice(1, 5)} ${s.slice(5, 8)} ${s.slice(8)}`;
    }
    if (s.length === 10) {
      return `+${s.slice(0, 2)} ${s.slice(2, 6)} ${s.slice(6)}`;
    }
    return `+${s}`;
  };

  const whatsappHref = `https://wa.me/${onlyDigits(currentLang.whatsappNumber)}`;
  const whatsappDisplay = formatWhatsAppDisplay(currentLang.whatsappNumber);

  const handleLinkClick = (e, path) => {
    e.preventDefault();
    window.location.href = `https://infynixsolution.com${path}`;
  };

  return (
    <footer
      style={{
        ...styles.footer,
        background: 'linear-gradient(to right, #0a0f2c, #111)',
        color: '#ddd',
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Amiri:wght@400&display=swap');
        `}
      </style>

      <div style={styles.container}>
        <div style={styles.columnGroup}>
          {/* Company Info */}
          <div style={styles.column}>
            <h3 style={{ ...styles.heading, fontFamily: language === 'ar' ? "'Amiri', serif" : "'Poppins', sans-serif" }}>
              {currentLang.companyName}
            </h3>
            <p style={{ ...styles.text, fontFamily: language === 'ar' ? "'Amiri', serif" : "'Poppins', sans-serif", fontWeight: 400 }}>
              {currentLang.companyDesc}
            </p>
          </div>

          {/* Contact Info */}
          <div style={styles.column}>
            <h4 style={{ ...styles.subHeading, fontFamily: language === 'ar' ? "'Amiri', serif" : "'Poppins', sans-serif" }}>
              {currentLang.contactHeading}
            </h4>

            <p style={styles.text}>
              <FaWhatsapp style={styles.icon} color="green" />
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" style={styles.link}>
                {whatsappDisplay}
              </a>
            </p>

            <p style={styles.text}>
              <FaEnvelope style={styles.icon} />
              <a href={`mailto:${currentLang.contactEmail}`} style={styles.link} aria-label={`Email ${currentLang.contactEmail}`}>
                {currentLang.contactEmail}
              </a>
            </p>

            <p style={styles.text}>
              <FaGlobe style={styles.icon} />
              <a href={`https://${currentLang.contactWebsite}`} target="_blank" rel="noopener noreferrer" style={styles.link}>
                {currentLang.contactWebsite}
              </a>
            </p>
          </div>

          {/* Company Links */}
          <div style={styles.column}>
            <h4 style={{ ...styles.subHeading, fontFamily: language === 'ar' ? "'Amiri', serif" : "'Poppins', sans-serif" }}>
              {currentLang.companyHeading}
            </h4>
            {Object.entries(currentLang.companyLinks).map(([key, value]) => (
              <p key={key} style={styles.text}>
                {key === 'home' ? (
                  <a href="https://infynixsolution.com" style={styles.link} onClick={(e) => handleLinkClick(e, '/')}>
                    {value}
                  </a>
                ) : (
                  <Link to={`/${key.toLowerCase()}`} style={styles.link} onClick={(e) => handleLinkClick(e, `/${key.toLowerCase()}`)}>
                    {value}
                  </Link>
                )}
              </p>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div style={styles.bottomBar}>
          <p style={styles.bottomText}>{currentLang.bottomText}</p>
        </div>
      </div>
    </footer>
  );
}

const styles = {
  footer: {
    padding: '60px 20px 30px',
    fontFamily: 'Poppins, sans-serif',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  columnGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '40px',
    justifyContent: 'space-between',
    marginBottom: '40px',
  },
  column: {
    flex: '1 1 250px',
  },
  heading: {
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '15px',
  },
  subHeading: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '10px',
  },
  text: {
    fontSize: '14px',
    lineHeight: '1.6',
    margin: '6px 0',
    display: 'flex',
    alignItems: 'center',
  },
  link: {
    color: '#00BFFF',
    textDecoration: 'none',
    transition: 'color 0.15s ease',
    marginLeft: 8,
    cursor: 'pointer',
  },
  bottomBar: {
    borderTop: '1px solid #333',
    paddingTop: '20px',
    textAlign: 'center',
  },
  bottomText: {
    fontSize: '13px',
    margin: '6px 0',
  },
  icon: {
    marginRight: 8,
    verticalAlign: 'middle',
  },
};